import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { useForm } from '@tanstack/react-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as validators from '@/shared/utils/validators'

export function OrganisationSettingsPage() {
  const { t } = useTranslation()
  const { data: orgSettings } = useQuery(
    convexQuery(api.features.org.org_settings.getMyOrgSettings, {}),
  )
  const { data: currentUser } = useQuery(
    convexQuery(api.features.user.user.getMyCurrentUser, {}),
  )
  const { mutateAsync: updateOrgSettings } = useMutation({
    mutationFn: useConvexMutation(
      api.features.org.org_settings.updateMyOrgSettings,
    ),
  })
  const { mutateAsync: updateSsoConfig } = useMutation({
    mutationFn: useConvexMutation(
      api.features.org.org_settings.updateMySsoConfig,
    ),
  })
  const { mutateAsync: updateSsoEnforcement } = useMutation({
    mutationFn: useConvexMutation(
      api.features.org.org_settings.updateMySsoEnforcement,
    ),
  })
  const { mutateAsync: clearSsoClientSecret } = useMutation({
    mutationFn: useConvexMutation(
      api.features.org.org_settings.clearMySsoClientSecret,
    ),
  })

  const isOwner = currentUser?.role === 'owner'
  const [isRotatingSecret, setIsRotatingSecret] = useState(false)

  const form = useForm({
    defaultValues: {
      displayName: orgSettings?.displayName ?? '',
      termsUrl: orgSettings?.termsUrl ?? '',
      privacyUrl: orgSettings?.privacyUrl ?? '',
    },
    onSubmit: async ({ value }) => {
      await updateOrgSettings({
        displayName: value.displayName,
        termsUrl: value.termsUrl || undefined,
        privacyUrl: value.privacyUrl || undefined,
      })
    },
  })

  const ssoForm = useForm({
    defaultValues: {
      ssoEmailDomain: orgSettings?.ssoEmailDomain ?? '',
      oidcDiscoveryUrl: orgSettings?.oidcDiscoveryUrl ?? '',
      oidcClientId: orgSettings?.oidcClientId ?? '',
      oidcClientSecret: '',
    },
    onSubmit: async ({ value }) => {
      await updateSsoConfig({
        ssoEmailDomain: value.ssoEmailDomain || undefined,
        oidcDiscoveryUrl: value.oidcDiscoveryUrl || undefined,
        oidcClientId: value.oidcClientId || undefined,
        oidcClientSecret: value.oidcClientSecret || undefined,
      })
      setIsRotatingSecret(false)
    },
  })

  const handleToggleEnforcement = async (): Promise<void> => {
    await updateSsoEnforcement({
      ssoEnforced: !orgSettings?.ssoEnforced,
    })
  }

  const handleRemoveSecret = async (): Promise<void> => {
    await clearSsoClientSecret({})
    setIsRotatingSecret(false)
  }

  const formatDate = (ms: number): string =>
    new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(ms))

  return (
    <div className="flex h-full w-full flex-col gap-6">
      <form
        className="flex w-full flex-col items-start rounded-lg border border-border bg-card"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
      >
        <div className="flex w-full flex-col gap-6 rounded-lg p-6">
          <div className="flex flex-col gap-2">
            <h2 className="text-xl font-medium text-primary">
              {t('settings.organisation.title')}
            </h2>
            <p className="text-sm font-normal text-primary/60">
              {t('settings.organisation.description')}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-primary">
              {t('settings.organisation.displayNameTitle')}
            </p>
            <p className="text-sm font-normal text-primary/60">
              {t('settings.organisation.displayNameDescription')}
            </p>
            <form.Field
              name="displayName"
              validators={{ onSubmit: validators.displayName }}
              children={(field) => (
                <Input
                  placeholder={t(
                    'settings.organisation.displayNamePlaceholder',
                  )}
                  autoComplete="organization"
                  required
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className={`w-80 bg-transparent ${
                    field.state.meta?.errors.length > 0 &&
                    'border-destructive focus-visible:ring-destructive'
                  }`}
                />
              )}
            />
            {(form.state.fieldMeta.displayName?.errors?.length ?? 0) > 0 && (
              <p className="text-sm text-destructive dark:text-destructive-foreground">
                {form.state.fieldMeta.displayName?.errors.join(' ')}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-primary">
              {t('settings.organisation.termsUrlTitle')}
            </p>
            <p className="text-sm font-normal text-primary/60">
              {t('settings.organisation.termsUrlDescription')}
            </p>
            <form.Field
              name="termsUrl"
              validators={{ onSubmit: validators.url }}
              children={(field) => (
                <Input
                  placeholder={t('settings.organisation.termsUrlPlaceholder')}
                  type="url"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className={`w-80 bg-transparent ${
                    field.state.meta?.errors.length > 0 &&
                    'border-destructive focus-visible:ring-destructive'
                  }`}
                />
              )}
            />
            {(form.state.fieldMeta.termsUrl?.errors?.length ?? 0) > 0 && (
              <p className="text-sm text-destructive dark:text-destructive-foreground">
                {form.state.fieldMeta.termsUrl?.errors.join(' ')}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-primary">
              {t('settings.organisation.privacyUrlTitle')}
            </p>
            <p className="text-sm font-normal text-primary/60">
              {t('settings.organisation.privacyUrlDescription')}
            </p>
            <form.Field
              name="privacyUrl"
              validators={{ onSubmit: validators.url }}
              children={(field) => (
                <Input
                  placeholder={t('settings.organisation.privacyUrlPlaceholder')}
                  type="url"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className={`w-80 bg-transparent ${
                    field.state.meta?.errors.length > 0 &&
                    'border-destructive focus-visible:ring-destructive'
                  }`}
                />
              )}
            />
            {(form.state.fieldMeta.privacyUrl?.errors?.length ?? 0) > 0 && (
              <p className="text-sm text-destructive dark:text-destructive-foreground">
                {form.state.fieldMeta.privacyUrl?.errors.join(' ')}
              </p>
            )}
          </div>
        </div>

        <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-secondary px-6 dark:bg-card">
          <p className="text-sm font-normal text-primary/60">
            {t('settings.organisation.hint')}
          </p>
          <Button type="submit" size="sm">
            {t('settings.organisation.save')}
          </Button>
        </div>
      </form>

      {isOwner && (
        <form
          className="flex w-full flex-col items-start rounded-lg border border-border bg-card"
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            ssoForm.handleSubmit()
          }}
        >
          <div className="flex w-full flex-col gap-6 rounded-lg p-6">
            <div className="flex flex-col gap-2">
              <h2 className="text-xl font-medium text-primary">
                {t('settings.organisation.sso.title')}
              </h2>
              <p className="text-sm font-normal text-primary/60">
                {t('settings.organisation.sso.description')}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium text-primary">
                {t('settings.organisation.sso.emailDomainTitle')}
              </p>
              <p className="text-sm font-normal text-primary/60">
                {t('settings.organisation.sso.emailDomainDescription')}
              </p>
              <ssoForm.Field
                name="ssoEmailDomain"
                children={(field) => (
                  <Input
                    placeholder={t(
                      'settings.organisation.sso.emailDomainPlaceholder',
                    )}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    className="w-80 bg-transparent"
                  />
                )}
              />
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium text-primary">
                {t('settings.organisation.sso.discoveryUrlTitle')}
              </p>
              <p className="text-sm font-normal text-primary/60">
                {t('settings.organisation.sso.discoveryUrlDescription')}
              </p>
              <ssoForm.Field
                name="oidcDiscoveryUrl"
                children={(field) => (
                  <Input
                    placeholder={t(
                      'settings.organisation.sso.discoveryUrlPlaceholder',
                    )}
                    type="url"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    className="w-80 bg-transparent"
                  />
                )}
              />
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium text-primary">
                {t('settings.organisation.sso.clientIdTitle')}
              </p>
              <p className="text-sm font-normal text-primary/60">
                {t('settings.organisation.sso.clientIdDescription')}
              </p>
              <ssoForm.Field
                name="oidcClientId"
                children={(field) => (
                  <Input
                    placeholder={t(
                      'settings.organisation.sso.clientIdPlaceholder',
                    )}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    className="w-80 bg-transparent"
                  />
                )}
              />
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium text-primary">
                {t('settings.organisation.sso.clientSecretTitle')}
              </p>
              <p className="text-sm font-normal text-primary/60">
                {t('settings.organisation.sso.clientSecretDescription')}
              </p>
              {orgSettings?.oidcClientSecretSet && !isRotatingSecret ? (
                <div className="flex flex-col gap-2">
                  <Input
                    type="password"
                    value="••••••••"
                    disabled
                    className="w-80 bg-transparent"
                  />
                  {orgSettings.oidcClientSecretSetAt && (
                    <p className="text-sm font-normal text-primary/60">
                      {t('settings.organisation.sso.clientSecretSetOn', {
                        date: formatDate(orgSettings.oidcClientSecretSetAt),
                      })}
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setIsRotatingSecret(true)}
                    >
                      {t('settings.organisation.sso.clientSecretRotate')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={handleRemoveSecret}
                    >
                      {t('settings.organisation.sso.clientSecretRemove')}
                    </Button>
                  </div>
                </div>
              ) : (
                <ssoForm.Field
                  name="oidcClientSecret"
                  children={(field) => (
                    <Input
                      placeholder={t(
                        'settings.organisation.sso.clientSecretPlaceholder',
                      )}
                      type="password"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      className="w-80 bg-transparent"
                    />
                  )}
                />
              )}
            </div>

            {orgSettings?.oidcDiscoveryUrl && (
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-primary">
                  {t('settings.organisation.sso.enforceTitle')}
                </p>
                <p className="text-sm font-normal text-primary/60">
                  {t('settings.organisation.sso.enforceDescription')}
                </p>
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    size="sm"
                    variant={orgSettings.ssoEnforced ? 'default' : 'outline'}
                    onClick={handleToggleEnforcement}
                  >
                    {orgSettings.ssoEnforced
                      ? t('settings.organisation.sso.enforced')
                      : t('settings.organisation.sso.notEnforced')}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-secondary px-6 dark:bg-card">
            <p className="text-sm font-normal text-primary/60">
              {t('settings.organisation.sso.hint')}
            </p>
            <Button type="submit" size="sm">
              {t('settings.organisation.sso.saveConfig')}
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}
